// =====================================================================================================================
// ========== IMPORTS
// =====================================================================================================================

import { useState, useEffect, useMemo, useCallback } from "react"
import { DataGrid } from "@mui/x-data-grid"
import Alert from "@mui/material/Alert"

// =====================================================================================================================
// ========== CONSTANTS
// =====================================================================================================================

const TODAY = new Date().toISOString().split("T")[0]

// =====================================================================================================================
// ========== HELPERS
// =====================================================================================================================

/**
 * Build a flat DataGrid row from a raw Supabase lease record (which includes nested relations).
 * The pivot over payment categories happens here in JS; we avoid a raw SQL pivot that PostgREST
 * cannot express natively.
 */
function buildRow(lease, categoryLabels) {
    const tenantNames = (lease.tenant ?? [])
        .map((t) =>
            t.company
                ? t.company.name
                : t.person
                    ? `${t.person.first_name} ${t.person.last_name}`
                    : ""
        )
        .filter(Boolean)
        .join(", ")

    // Only count buckets that are still active
    const activeBuckets = (lease.transactions_bucket ?? []).filter(
        (b) => !b.valid_until || b.valid_until >= TODAY
    )

    const bucketValues = Object.fromEntries(
        categoryLabels.map((label) => [
            `value_${label.toLowerCase()}`,
            activeBuckets.find((b) => b.category === label)?.default_value ?? 0,
        ])
    )

    return {
        id: lease.id,
        short_name: lease.property?.short_name ?? "",
        tenants: tenantNames,
        payment_frequency: lease.payment_frequency ?? "",
        ...bucketValues,
    }
}

// =====================================================================================================================
// ========== LEASES TABLE
//
// Rationale:
//  • All data logic (fetch + realtime) lives here, not in the parent.
//  • We use a single Supabase select with embedded relations (one round-trip) instead of multiple queries.
//  • Realtime subscriptions use a `refreshCounter` increment rather than passing the async fetch function
//    directly into the subscription callback — this avoids stale-closure bugs: the effect that actually
//    runs the fetch always captures the current closure.
//  • `rowSelectionModel` is controlled by the parent's `selectedLeaseId` so that selection survives
//    realtime-triggered data re-fetches without visually deselecting the active row.
// =====================================================================================================================

export default function LeasesTable({ supabase, selectedLeaseId, onSelect }) {

    const [rows, setRows]           = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading]     = useState(true)
    const [error, setError]         = useState(null)

    // Incrementing this triggers the fetch effect without stale-closure issues in the realtime callbacks.
    const [refreshCounter, setRefreshCounter] = useState(0)
    const triggerRefresh = useCallback(() => setRefreshCounter((c) => c + 1), [])

    // ── DATA FETCH ──────────────────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        let cancelled = false // guard against setting state on unmounted component

        const fetchData = async () => {
            // Do not flicker the skeleton on background refreshes — only show it on the initial load.
            if (rows.length === 0) setLoading(true)
            setError(null)

            try {
                // 1. Fetch payment categories from the settings table.
                //    We prefer the row that has an owner (user-specific) over the global default.
                const { data: settingData } = await supabase
                    .from("setting")
                    .select("value")
                    .eq("label", "transactions_buckets_categories")
                    .order("row_owner_id", { nullsFirst: false }) // non-null owner rows first
                    .limit(1)
                    .maybeSingle()

                const categoryLabels = settingData?.value?.split("|") ?? []

                // 2. Fetch active leases with all nested data in one round-trip.
                //    Filtering valid_until on the nested relation via PostgREST OR syntax is fragile,
                //    so we fetch all buckets and filter in JS inside buildRow().
                const { data: leases, error: leasesError } = await supabase
                    .from("lease")
                    .select(`
                        id,
                        payment_frequency,
                        end_date,
                        property:property_id (id, short_name),
                        tenant (
                            id,
                            company_id,
                            person_id,
                            company:company_id (id, name),
                            person:person_id  (id, first_name, last_name)
                        ),
                        transactions_bucket (id, category, default_value, valid_until)
                    `)
                    .or(`end_date.is.null,end_date.gte.${TODAY}`)

                if (leasesError) throw leasesError

                if (!cancelled) {
                    setCategories(categoryLabels)
                    setRows((leases ?? []).map((l) => buildRow(l, categoryLabels)))
                }
            } catch (err) {
                if (!cancelled) setError(err.message ?? "Erreur lors du chargement des baux.")
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        fetchData()
        return () => { cancelled = true }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshCounter]) // only refreshCounter drives re-fetches; supabase ref is stable

    // ── REALTIME SUBSCRIPTIONS ───────────────────────────────────────────────────────────────────────────

    useEffect(() => {
        // Subscribe to changes on the three tables that affect the list view.
        // On any change we simply increment the counter; the fetch effect above does the rest.
        const channel = supabase
            .channel("leases_table_realtime")
            .on("postgres_changes", { event: "*", schema: "public", table: "lease" },              triggerRefresh)
            .on("postgres_changes", { event: "*", schema: "public", table: "tenant" },             triggerRefresh)
            .on("postgres_changes", { event: "*", schema: "public", table: "transactions_bucket" }, triggerRefresh)
            .subscribe()

        return () => { supabase.removeChannel(channel) }
    }, [supabase, triggerRefresh])

    // ── COLUMNS (memoised — only recomputed when categories change) ──────────────────────────────────────

    const columns = useMemo(() => [
        { field: "short_name",        headerName: "Propriété",  flex: 1 },
        { field: "tenants",           headerName: "Locataires", flex: 2 },
        { field: "payment_frequency", headerName: "Fréquence",  flex: 1 },
        ...categories.map((label) => ({
            field: `value_${label.toLowerCase()}`,
            headerName: label,
            flex: 1,
            type: "number",
        })),
    ], [categories])

    // ── RENDER ───────────────────────────────────────────────────────────────────────────────────────────

    if (error) return <Alert severity="error">{error}</Alert>

    return (
        <DataGrid
            autoHeight
            checkboxSelection
            disableMultipleRowSelection
            loading={loading}
            rows={rows}
            columns={columns}
            // Controlled selection — keeps the highlighted row after realtime re-fetches.
            rowSelectionModel={selectedLeaseId ? [selectedLeaseId] : []}
            onRowSelectionModelChange={(model) =>
                onSelect(model.length ? model[0] : null)
            }
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            initialState={{
                pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            density="compact"
        />
    )
}